import React, { ReactNode, useState, useMemo } from "react";
import {
  Table,
  Dimmer,
  Label,
  MenuItem,
  Popup,
  PaginationProps,
  Icon,
  Button,
  Modal,
} from "semantic-ui-react";
import {
  StyledSemanticTabs,
  StyledTabPaneNoBorder,
  TableContainer,
  formatAggregateValue,
  formatUtcDateTime,
  parseUtcDateTime,
} from "../../Dashboards/Panel/util";
import { Alert, AlertsData } from "../../Dashboards/Panel/Alerts/PanelDef";
import { capitalizeFirstLetter } from "../../util";
import { AlertRule, validateWholeNumber } from "../../../../util";
import { AbsoluteTimestamp } from "../../Dashboards/Datetime/Timestamp";
import {
  AbsoluteTimeRange,
  TimeRange,
} from "../../Dashboards/Datetime/TimeRange";
import { DashboardsInfo } from "../../Dashboards/ViewDashboard";
import {
  StyledCardSearchPageInput,
  StyledPagination,
  StyledSecondaryDevicePerPageWidget,
} from "../../../common/commonStyledComps";
import { colorMap } from "../../Dashboards/Panel/Alerts/ViewAlerts";
import { beamtoast } from "../../../common/CustomToast";
import { SelectDevicesPerPage } from "../../DeviceManagement/Devices/Devices";
import { rowsPerPageOptions } from "../../../../BytebeamClient";
import moment from "moment";
import styled from "styled-components";
import LoadingAnimation from "../../../common/Loader";

export interface AlertAggregate {
  name: string; //name of the aggregate
  table: string;
  column: string;
  aggregator?: string; // aggregator type (e.g:"sum", "avg")
}

//This interface unifies props for ALL alert display scenarios:
export interface SharedAlertsViewProps {
  data: AlertsData; //Complete alerts dataset with alerts, rules, and summary
  loading: boolean; //Loading state for data fetching
  alertGroups: string[]; //Array of alert group names for organization
  aggregates: AlertAggregate[]; // Array of aggreagtes columns to display in the table
  groupByAlertGroups: boolean; //grouping strategy for alerts
  activePage: number;
  setActivePage: (page: number) => void;
  totalPages: number;
  alertsPerPage: number;
  setAlertsPerPage: (perPage: number) => void;
  changeAlertsPerPage?: (event: any, data: any) => void;
  handlePageChange: (
    //pagination event handler
    event: React.MouseEvent<HTMLAnchorElement>,
    data: PaginationProps
  ) => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  dashboards: DashboardsInfo[]; //array of dashboards link metadata
  selectedFilters?: string[]; // Array of selected filter values
  tables?: Record<string, any[]>; // Table schema for formatting
  tabbedView?: boolean; // true for tabbed view, false for card view
}

//context interface for alerts view
export interface AlertsViewContext {
  isPanel: boolean; // true if viewing in a panel, false for dashboard view
  panelMeta?: any; // Metadata for the panel if isPanel is true
  dashboardId?: string | number; // Dashboard ID for navigation
  fetchParams?: any; // Parameters for fetching alerts data, e.g., time range, filters, etc.
  timeRange?: TimeRange; // current time range for the alerts
  onTimeRangeChange?: (timeRange: TimeRange) => void; //time range change handler
}

// tab information structure for rendering tabs in the alerts view
export interface AlertTabInfo {
  name: string; // Tab display name
  count: number; // Alert count for badge
  criticality?: string; // For color coding
  alerts: Alert[]; // Alerts in this tab
}

//grouped alerts structure , Key: Group name or rule name, Value: Array of alerts belonging to that group/rule
export interface GroupedAlerts {
  [key: string]: Alert[];
}

// styled components
export const StyledDashboardLink = styled(Button)`
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  color: ${(props) => props.theme.colors["link-text-color"]} !important;
  margin-right: 0px !important;
  padding: 8px 12px !important;
  white-space: nowrap !important;
  font-size: 12px !important;
  margin-right: 16px !important;

  .icon {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin-right: 6px !important;
  }
  &:hover {
    opacity: 0.8 !important;
  }
`;

const AlertCardComponent = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "backgroundColor",
})<{
  backgroundColor?: string;
}>`
  height: 80px;
  width: calc(100% - 10px);
  padding: 20px;
  color: ${({ theme, backgroundColor }) =>
    theme.colors["text"] !== "#000000"
      ? "white"
      : backgroundColor
        ? "white"
        : "black"};
  background-color: ${({ backgroundColor }) => backgroundColor};
  border: ${({ backgroundColor }) =>
    backgroundColor ? "none" : "1px solid #CCCCCC"};
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 20px;
  border-radius: 10px;
  cursor: pointer;
`;

const TextContainer = styled.div`
  padding: 8px 0px;
  font-size: 22px;
  margin-left: 25px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const groupAlertsByGroupName = (data: AlertsData, groupNames: string[]) => {
  const groupNamesSet = new Set(groupNames);
  const groupedAlerts = {};

  // Initialize all group names with an empty array
  groupNamesSet.forEach((name) => {
    groupedAlerts[name] = [];
  });

  // Filter alert rules based on the group names
  const filteredAlertRules =
    data.alertRules?.filter((rule) => groupNamesSet.has(rule.group)) || [];

  // Group alerts by their group name
  filteredAlertRules.forEach((rule) => {
    const groupName = rule.group;
    const matchingAlerts =
      data.alerts?.filter((alert) => alert.alert_rule?.id === rule?.id) || [];

    groupedAlerts[groupName] = groupedAlerts[groupName].concat(matchingAlerts);
  });

  return groupedAlerts;
};

const groupAlertsByAlertRuleName = (data: AlertsData, ruleNames: string[]) => {
  const alertRuleNamesSet = new Set(ruleNames);
  const groupedAlerts = {};

  // Initialize all rule names with an empty array
  ruleNames.forEach((name) => {
    groupedAlerts[name] = [];
  });

  // Filter alert rules based on the provided alert rule names
  const matchedAlertRules =
    data?.alertRules?.filter((rule) => {
      return alertRuleNamesSet.has(rule?.name);
    }) || [];

  // Group alerts by the matched alert rule names
  matchedAlertRules.forEach((rule) => {
    const ruleName = rule?.name;
    const matchingAlerts =
      data?.alerts?.filter((alert) => alert?.alert_rule.id === rule?.id) || [];

    groupedAlerts[ruleName] = groupedAlerts[ruleName].concat(matchingAlerts);
  });

  return groupedAlerts;
};

// Dashboard Links Component - Consistent styling for both dashboard and panel
type AlertsToDashboardLinksProps = {
  readonly dashboardIds: string[];
  readonly allDashboards: DashboardsInfo[];
  readonly timeRange?: TimeRange;
  readonly deviceId?: string;
};

function AlertsToDashboardLinks(props: AlertsToDashboardLinksProps) {
  const { dashboardIds, allDashboards, timeRange, deviceId } = props;

  return (
    <div className="dashboard-links">
      {dashboardIds.map((id) => {
        const dashboard = allDashboards.find(
          (dashboard) => String(dashboard.id) === id
        );

        if (dashboard) {
          const dashboardURL = `${window.location.href.split("/alerts")[0]}/dashboards/${id}`;
          const url = new URL(dashboardURL);

          // Preserve context in URL parameters
          if (deviceId) {
            url.searchParams.set("id", deviceId);
          }
          if (timeRange) {
            const timeRangeStr = JSON.stringify(timeRange.serialize());
            url.searchParams.set("timeRange", timeRangeStr);
          }

          return (
            <StyledDashboardLink
              key={id}
              as="a"
              href={url.toString()}
              target="_blank"
              icon="external"
              secondary
            >
              <Icon name="external" />
              <span>{`${dashboard?.title} (${dashboard?.type})`}</span>
            </StyledDashboardLink>
          );
        } else {
          return null;
        }
      })}
    </div>
  );
}

//ALERT CARD COMPONENT
type AlertCardProps = {
  readonly tabName: string;
  readonly totalCount: number;
  readonly background?: string;
  readonly onClick: () => void;
};

function AlertCard(props: AlertCardProps) {
  const numberStyle = {
    fontSize: "50px",
    fontWeight: "bold",
  } as React.CSSProperties;

  return (
    <AlertCardComponent
      backgroundColor={props.background}
      onClick={props.onClick}
    >
      <div style={numberStyle}>{props.totalCount}</div>
      <TextContainer>{props.tabName}</TextContainer>
    </AlertCardComponent>
  );
}

//Alert List Modal Component
interface AlertListModalProps {
  readonly alerts: Alert[];
  readonly tabName: string;
  readonly trigger: React.ReactNode;
  readonly loading: boolean;
  readonly totalPages: number;
  readonly currentPage: number;
  readonly handlePaginationChange: (
    event: React.MouseEvent<HTMLAnchorElement>,
    data: PaginationProps
  ) => void;
  readonly handlePaginationInputChange: (event: any) => void;
  readonly handlePaginationInputKeyDown: (
    event: any,
    totalPages?: number
  ) => void;
  readonly inputPageNumber: number;
  readonly alertsPerPage: number;
  readonly changeAlertsPerPage: (event: any, data: any) => void;
  readonly aggregates: AlertAggregate[];
  readonly alertRules?: AlertRule[];
  readonly dashboards: DashboardsInfo[];
  readonly tables?: Record<string, any[]>;
  readonly isGroupedByAlertGroups?: boolean;
}

function AlertListModal(props: AlertListModalProps) {
  const [open, setOpen] = React.useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Filter alerts based on tab name or alert groups
  // If grouped by alert groups, show all alerts in the group
  // If not grouped, filter by alert rule name matching the tab name
  const filteredAlerts = useMemo(() => {
    return props.isGroupedByAlertGroups
      ? props.alerts
      : props.alerts.filter(
          (alert) => alert.alert_rule?.name === props.tabName
        );
  }, [props.alerts, props.isGroupedByAlertGroups, props.tabName]);

  const displayAlerts = filteredAlerts;

  const serialMetadataKey =
    displayAlerts.length > 0 &&
    displayAlerts[0]["-serial_metadata"] &&
    Object.keys(displayAlerts[0]["-serial_metadata"]).length > 0 &&
    capitalizeFirstLetter(Object.keys(displayAlerts[0]["-serial_metadata"])[0]);

  return (
    <Modal
      className="dark"
      onClose={handleClose}
      onOpen={handleOpen}
      open={open}
      size="large"
      trigger={props.trigger}
    >
      <Modal.Header>{props.tabName} List</Modal.Header>
      <Modal.Content>
        <TableContainer style={{ paddingTop: "10px" }}>
          <div className="tableContentContainer">
            <Dimmer.Dimmable style={{ zIndex: 1 }} dimmed={props.loading}>
              <Dimmer active={props.loading}>
                <LoadingAnimation
                  loadingText="Loading"
                  loaderSize="42px"
                  loaderColor="#FFFFFF"
                  textColor="#FFFFFF"
                />
              </Dimmer>
              <AlertsTable
                alerts={displayAlerts}
                alertRules={props.alertRules}
                aggregates={props.aggregates}
                dashboards={props.dashboards}
                tables={props.tables}
                showGroupName={props.isGroupedByAlertGroups}
                serialMetadataKey={serialMetadataKey}
              />
              {!props.loading && props.totalPages > 0 && (
                <AlertsPagination
                  currentPage={props.currentPage}
                  totalPages={props.totalPages}
                  handlePaginationChange={props.handlePaginationChange}
                  handlePaginationInputChange={
                    props.handlePaginationInputChange
                  }
                  handlePaginationInputKeyDown={
                    props.handlePaginationInputKeyDown
                  }
                  inputPageNumber={props.inputPageNumber}
                  alertsPerPage={props.alertsPerPage}
                  changeAlertsPerPage={props.changeAlertsPerPage}
                  isModal={true}
                />
              )}
            </Dimmer.Dimmable>
          </div>
        </TableContainer>
      </Modal.Content>
      <Modal.Actions>
        <Button primary onClick={handleClose}>
          Close
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

// AlertsTable Component
interface AlertsTableProps {
  alerts: Alert[];
  alertRules?: AlertRule[];
  aggregates: AlertAggregate[];
  dashboards: DashboardsInfo[];
  tables?: Record<string, any[]>;
  showGroupName?: boolean;
  serialMetadataKey?: string | false;
}

function AlertsTable(props: AlertsTableProps) {
  const {
    alerts,
    alertRules,
    aggregates,
    dashboards,
    tables,
    serialMetadataKey,
  } = props;

  return (
    <Table sortable={false} compact selectable unstackable size="small">
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell style={{ cursor: "unset" }} textAlign="center">
            {serialMetadataKey ? `#${serialMetadataKey}` : "Device ID"}
          </Table.HeaderCell>
          <Table.HeaderCell style={{ cursor: "unset" }} textAlign="center">
            Rule Name
          </Table.HeaderCell>
          <Table.HeaderCell style={{ cursor: "unset" }} textAlign="center">
            Start Time
          </Table.HeaderCell>
          <Table.HeaderCell style={{ cursor: "unset" }} textAlign="center">
            End Time
          </Table.HeaderCell>
          <Table.HeaderCell style={{ cursor: "unset" }} textAlign="center">
            Duration
          </Table.HeaderCell>
          {aggregates.map((agg) => (
            <Table.HeaderCell key={agg.name} style={{ cursor: "unset" }}>
              {agg.name}
            </Table.HeaderCell>
          ))}
          <Table.HeaderCell style={{ cursor: "unset" }} textAlign="center">
            Dashboard Link
          </Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {alerts.length > 0 ? (
          alerts.map((alert) => {
            // Find associated dashboard for this alert
            const dashboardId = alertRules?.find(
              (rule) => rule?.id === alert?.alert_rule?.id
            )?.deepdive_dashboard_id;

            return (
              <Table.Row key={alert.id}>
                {alert?.["-serial_metadata"] ? (
                  <Popup
                    inverted
                    hideOnScroll
                    position="top left"
                    content={
                      <>
                        {alert?.["-serial_metadata"] && (
                          <p>Device ID: {alert?.device_id}</p>
                        )}
                        {alert?.metadata &&
                        Object.entries(alert.metadata).length > 0
                          ? Object.entries(alert.metadata).map(
                              ([key, value]) => {
                                return (
                                  <p key={key}>
                                    {value ? `${key} : ${value}` : <></>}
                                  </p>
                                );
                              }
                            )
                          : "No metadata"}
                      </>
                    }
                    trigger={
                      <Table.Cell textAlign="center">
                        {
                          Object.values(
                            alert?.["-serial_metadata"]
                          )[0] as ReactNode
                        }
                      </Table.Cell>
                    }
                  />
                ) : (
                  <Table.Cell textAlign="center">{alert?.device_id}</Table.Cell>
                )}

                <Table.Cell textAlign="center">
                  {alert.alert_rule.name}
                </Table.Cell>

                <Table.Cell textAlign="center">
                  {formatUtcDateTime(alert.start_time)}
                </Table.Cell>
                <Table.Cell textAlign="center">
                  {formatUtcDateTime(alert.end_time)}
                </Table.Cell>
                <Table.Cell textAlign="center">
                  {moment
                    .duration(alert.end_time - alert.start_time)
                    .humanize({ ss: -1 })}
                </Table.Cell>
                {aggregates.map((agg) => {
                  const value = alert.aggregates[agg.name];

                  return (
                    <Table.Cell key={agg.name} textAlign="center">
                      {formatAggregateValue(value, agg, tables) || "--"}
                    </Table.Cell>
                  );
                })}
                <Table.Cell textAlign="center">
                  {dashboardId ? (
                    <AlertsToDashboardLinks
                      dashboardIds={[dashboardId]}
                      allDashboards={dashboards}
                      deviceId={String(alert?.device_id)}
                      timeRange={(() => {
                        const start = parseUtcDateTime(alert.start_time);
                        const end = parseUtcDateTime(alert.end_time);
                        if (!start || !end) return undefined;
                        return new AbsoluteTimeRange(
                          new AbsoluteTimestamp(start),
                          new AbsoluteTimestamp(end)
                        );
                      })()}
                    />
                  ) : (
                    "--"
                  )}
                </Table.Cell>
              </Table.Row>
            );
          })
        ) : (
          //when no alerts are available
          <Table.Row>
            <Table.Cell colSpan={6 + aggregates.length} textAlign="center">
              No alerts data available
            </Table.Cell>
          </Table.Row>
        )}
      </Table.Body>
    </Table>
  );
}

// Pagination Component
interface AlertsPaginationProps {
  currentPage: number;
  totalPages: number;
  handlePaginationChange: (
    event: React.MouseEvent<HTMLAnchorElement>,
    data: PaginationProps
  ) => void;
  handlePaginationInputChange: (event: any) => void;
  handlePaginationInputKeyDown: (event: any, totalPages?: number) => void;
  inputPageNumber: number;
  alertsPerPage: number;
  changeAlertsPerPage: (event: any, data: any) => void;
  isModal?: boolean;
}

function AlertsPagination(props: AlertsPaginationProps) {
  const {
    currentPage,
    totalPages,
    handlePaginationChange,
    handlePaginationInputChange,
    handlePaginationInputKeyDown,
    inputPageNumber,
    alertsPerPage,
    changeAlertsPerPage,
    isModal = false,
  } = props;

  // Select appropriate options based on context
  const options = rowsPerPageOptions;

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        marginTop: isModal ? "10px" : "10px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <StyledPagination //page navigation component
          boundaryRange={0}
          ellipsisItem={null}
          siblingRange={1}
          activePage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePaginationChange}
        />
        <StyledCardSearchPageInput //jump to page input component
          icon="search"
          placeholder="Jump to page..."
          name="activePage"
          min={1}
          onChange={handlePaginationInputChange}
          onKeyDown={(event) => handlePaginationInputKeyDown(event, totalPages)}
          type="number"
          value={inputPageNumber || ""}
        />
        {/*rows per page selection component*/}
        <StyledSecondaryDevicePerPageWidget>
          <MenuItem>{isModal ? "Rows per page" : "Alerts per page"}</MenuItem>
          <MenuItem style={{ padding: "0px" }}>
            <SelectDevicesPerPage
              compact
              selection
              upward={!isModal}
              pointing={isModal ? "bottom" : undefined}
              options={options}
              value={alertsPerPage}
              onChange={changeAlertsPerPage}
              style={{ minWidth: "80px" }}
              text={alertsPerPage.toString()} //force displaying the selected value
              fluid //ensure proper width
            />
          </MenuItem>
        </StyledSecondaryDevicePerPageWidget>
      </div>
    </div>
  );
}

//main component
interface AlertsComponentProps extends SharedAlertsViewProps {
  context: AlertsViewContext;
  onTabChange?: (tabName: string) => void;
  onRefreshData?: (
    pageNumber: number,
    alertsPerPage: number,
    tabName: string
  ) => void;
}

const AlertsComponent: React.FC<AlertsComponentProps> = ({
  data,
  alertGroups,
  aggregates,
  loading,
  totalPages,
  dashboards,
  handlePageChange,
  activePage,
  setActivePage,
  activeTab,
  setActiveTab,
  groupByAlertGroups,
  alertsPerPage,
  setAlertsPerPage,
  changeAlertsPerPage,
  selectedFilters = [],
  tabbedView = true,
  tables = {},
  context,
  onTabChange,
  onRefreshData,
}) => {
  //state for active tab and pagination input
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [inputPageNumber, setInputPageNumber] = useState<number>(0);

  //Extract data from props
  const alerts = data?.alerts ?? [];
  const alertRules = data?.alertRules ?? [];
  const summary = data?.summary ?? {};

  //Calculating total count for "All Alerts" tab
  const allAlertsTotalCount = useMemo(() => {
    return Object.entries(summary).reduce(
      (sum, [, rules]) =>
        sum +
        Object.values(rules).reduce((sum, ruleInfo) => sum + ruleInfo.count, 0),
      0
    );
  }, [summary]);

  //tab information based on alert rules or alert groups
  const createTabInfo = (): AlertTabInfo[] => {
    if (groupByAlertGroups) {
      // For alert groups: Get all unique groups from alertRules (not summary)
      const allGroups = Array.from(
        new Set(alertRules.map((rule) => rule.group))
      );

      return allGroups.map((groupName) => {
        // Calculate total count for this group from summary
        let totalCount = 0;
        const alertGroupSummary = summary[groupName];
        if (alertGroupSummary) {
          Object.keys(alertGroupSummary).forEach(
            (rule) => (totalCount += alertGroupSummary[rule].count)
          );
        }

        return {
          name: groupName,
          count: totalCount,
          criticality: "",
          alerts: [],
        };
      });
    } else {
      //using alertRules array to ensure ALL rules appear
      return alertRules.map((rule) => {
        // Look up count and criticality for this rule in summary
        let totalCount = 0;
        let criticality = "";

        // Search through all groups in summary to find this rule
        Object.keys(summary).forEach((group) => {
          Object.keys(summary[group]).forEach((ruleName) => {
            if (ruleName === rule.name) {
              totalCount = summary[group][ruleName].count;
              criticality = summary[group][ruleName].criticality;
            }
          });
        });

        return {
          name: rule.name,
          count: totalCount,
          criticality: criticality || rule.criticality,
          alerts: [],
        };
      });
    }
  };

  // Create and sort tab information
  const tabInfo = useMemo(() => {
    const tabs = createTabInfo();

    // sorting active rules first, then inactive, then alphabetically
    return tabs.sort((a, b) => {
      if (a.count > 0 && b.count === 0) return -1;
      if (a.count === 0 && b.count > 0) return 1;
      if (a.count === b.count) {
        return a.name.localeCompare(b.name);
      }
      return b.count - a.count;
    });
  }, [alertRules, summary, groupByAlertGroups]); // eslint-disable-line react-hooks/exhaustive-deps

  // Create grouped alerts for filtering
  const tabbedAlerts = useMemo(() => {
    if (!data) return {};

    const tabList = tabInfo.map((tab) => tab.name);

    return groupByAlertGroups
      ? groupAlertsByGroupName(data, tabList)
      : groupAlertsByAlertRuleName(data, tabList);
  }, [data, tabInfo, groupByAlertGroups]);

  // Pagination handlers
  const handlePaginationInputChange = async (event: any) => {
    const newValue = event.target.value;
    setInputPageNumber(newValue);
  };

  const handlePaginationInputKeyDown = (
    event: any,
    totalPagesParam?: number
  ) => {
    const totalPagesToUse = totalPagesParam || totalPages;
    if (event.key === "Enter" || event.keyCode === 13) {
      if (validateWholeNumber(inputPageNumber.toString())) {
        let pageToGo = Number(inputPageNumber);
        if (pageToGo <= 0) {
          pageToGo = 1;
        } else if (pageToGo > totalPagesToUse) {
          pageToGo = totalPagesToUse;
        }
        handlePageChange(event, {
          activePage: pageToGo,
          totalPages: totalPagesToUse,
        });
        setInputPageNumber(0);
      } else {
        beamtoast.error("Please enter whole number for jump to page.");
      }
    }
  };

  const handleTabClick = (tabName: string, index: number) => {
    // Prevent duplicate tab switches
    if (activeTab === tabName) return;
    setActiveTab(tabName);
    setActiveIndex(index);
    setActivePage(1);

    // Notify parent components of tab change
    if (onTabChange) {
      onTabChange(tabName);
    }
  };

  const serialMetadataKey =
    alerts.length > 0 &&
    alerts[0]["-serial_metadata"] &&
    Object.keys(alerts[0]["-serial_metadata"]).length > 0 &&
    capitalizeFirstLetter(Object.keys(alerts[0]["-serial_metadata"])[0]);

  const tabs = [
    //all alerts tab always is first in the list and shows all alerts count
    {
      menuItem: (
        <MenuItem
          active={activeTab === "All Alerts"}
          key="All Alerts"
          onClick={() => handleTabClick("All Alerts", 0)}
        >
          All Alerts
          <Label style={{ background: "#65c369" }}>{allAlertsTotalCount}</Label>
        </MenuItem>
      ),
      render: () => (
        <StyledTabPaneNoBorder
          key="All Alerts"
          {...(context.isPanel && { isalertspaneltablesticky: true })}
          style={{ padding: "0px" }}
        >
          <TableContainer
            style={{
              padding: context.isPanel ? "15px" : "10px 0 0 0",
            }}
          >
            <div className="tableContentContainer" style={{ padding: "0px" }}>
              <Dimmer.Dimmable style={{ zIndex: 1 }} dimmed={loading}>
                <Dimmer active={loading}>
                  <LoadingAnimation
                    loadingText="Loading"
                    loaderSize="42px"
                    loaderColor="#FFFFFF"
                    textColor="#FFFFFF"
                  />
                </Dimmer>
                <AlertsTable
                  alerts={alerts}
                  alertRules={alertRules}
                  aggregates={aggregates}
                  dashboards={dashboards}
                  tables={tables}
                  showGroupName={groupByAlertGroups}
                  serialMetadataKey={serialMetadataKey}
                />
                {alerts.length > 0 && (
                  <AlertsPagination
                    currentPage={activePage}
                    totalPages={totalPages}
                    handlePaginationChange={handlePageChange}
                    handlePaginationInputChange={handlePaginationInputChange}
                    handlePaginationInputKeyDown={handlePaginationInputKeyDown}
                    inputPageNumber={inputPageNumber}
                    alertsPerPage={alertsPerPage}
                    changeAlertsPerPage={changeAlertsPerPage || (() => {})}
                  />
                )}
              </Dimmer.Dimmable>
            </div>
          </TableContainer>
        </StyledTabPaneNoBorder>
      ),
    },

    // Individual tabs for each rule/group
    ...tabInfo.map((tab, key) => {
      const { name: tabName, count: totalCount, criticality } = tab;

      // Get alerts for this specific tab
      const alertsForThisTab = tabbedAlerts[tabName] || [];

      // Determining tab color based on criticality
      let background = "";
      if (totalCount !== 0) {
        background = criticality ? colorMap[criticality] : colorMap["info"];
      }

      return {
        menuItem: (
          <MenuItem
            key={tabName}
            active={activeTab === tabName}
            onClick={() => handleTabClick(tabName, key + 1)}
          >
            {tabName}
            <Label style={{ background }}>{totalCount}</Label>
          </MenuItem>
        ),
        render: () => (
          <StyledTabPaneNoBorder
            key={tabName}
            {...(context.isPanel && { isalertspaneltablesticky: true })}
            style={{ padding: "0px" }}
          >
            <TableContainer
              style={{
                padding: context.isPanel ? "15px" : "10px 0 0 0",
              }}
            >
              <div
                className="tableContentContainer"
                style={{ padding: context.isPanel ? "0px" : "10px" }}
              >
                <Dimmer.Dimmable style={{ zIndex: 1 }} dimmed={loading}>
                  <Dimmer active={loading}>
                    <LoadingAnimation
                      loadingText="Loading"
                      loaderSize="42px"
                      loaderColor="#FFFFFF"
                      textColor="#FFFFFF"
                    />
                  </Dimmer>
                  <AlertsTable
                    alerts={alertsForThisTab}
                    alertRules={alertRules}
                    aggregates={aggregates}
                    dashboards={dashboards}
                    tables={tables}
                    serialMetadataKey={serialMetadataKey}
                  />
                  {alertsForThisTab.length > 0 && ( // Show pagination controls only if there are alerts for this tab
                    <AlertsPagination
                      currentPage={activePage}
                      totalPages={totalPages}
                      handlePaginationChange={handlePageChange}
                      handlePaginationInputChange={handlePaginationInputChange}
                      handlePaginationInputKeyDown={
                        handlePaginationInputKeyDown
                      }
                      inputPageNumber={inputPageNumber}
                      alertsPerPage={alertsPerPage}
                      changeAlertsPerPage={changeAlertsPerPage || (() => {})}
                    />
                  )}
                </Dimmer.Dimmable>
              </div>
            </TableContainer>
          </StyledTabPaneNoBorder>
        ),
      };
    }),
  ];

  //container style based on context
  const containerStyle = {
    overflowY: "scroll" as const,
    marginTop: context.isPanel ? "50px" : "10px",
    marginLeft: context.isPanel ? "20px" : "0px",
    marginRight: context.isPanel ? "20px" : "0px",
    height: context.isPanel ? "calc(100% - 50px)" : "auto",
  };

  if (context.isPanel && !tabbedView) {
    return (
      <div style={containerStyle}>
        {tabInfo.map((tab, key) => {
          const { name: tabName, count: totalCount, criticality } = tab;
          const alertsForThisTab = tabbedAlerts[tabName] || [];

          const background =
            totalCount !== 0
              ? criticality
                ? colorMap[criticality]
                : colorMap["info"]
              : "";

          return (
            <AlertListModal
              key={key}
              tabName={tabName}
              alerts={alertsForThisTab}
              aggregates={aggregates}
              loading={loading}
              totalPages={totalPages}
              currentPage={activePage}
              handlePaginationChange={handlePageChange}
              handlePaginationInputChange={handlePaginationInputChange}
              handlePaginationInputKeyDown={handlePaginationInputKeyDown}
              inputPageNumber={inputPageNumber}
              alertsPerPage={alertsPerPage}
              changeAlertsPerPage={changeAlertsPerPage || (() => {})}
              trigger={
                <AlertCard
                  tabName={tabName}
                  totalCount={totalCount}
                  background={background}
                  onClick={() => handleTabClick(tabName, key + 1)}
                />
              }
              alertRules={alertRules}
              dashboards={dashboards}
              tables={tables}
              isGroupedByAlertGroups={groupByAlertGroups}
            />
          );
        })}
      </div>
    );
  }

  //tabbed view rendering
  return (
    <div style={containerStyle}>
      <StyledSemanticTabs
        panes={tabs}
        activeIndex={activeIndex}
        background_color={"transparent"}
        style={{ maxWidth: context.isPanel ? "100%" : "80vw" }}
      />
    </div>
  );
};

export default AlertsComponent;
